// Foundations
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { map, skip, switchMap, take } from 'rxjs/operators';

// Models
import { FranchiseLocation } from '../models/franchise.model';
import { OrderRequestItem } from '../models/package/order-request.model';
import { PackageItem, SubItem } from '../models/package/package-item.model';
import { ServiceTypes } from '../models/reservation/reservation-types';

// Services
import { AddOrderGQL, GetAvailableItemsGQL, Item } from './graphql.service';
import { MyProfileService } from './my-profile.service';

import * as lodash from 'lodash';

@Injectable({
  providedIn: 'root',
})
export class PackageService {
  // #region -> Property <-
  // ----------

  /*
    Temp Location Selection
  */
  newOptionFromSearch: FranchiseLocation;
  preferredLocation: FranchiseLocation;

  /*
    Packages
  */
  packageList: PackageItem[] = [];
  packageList$ = new BehaviorSubject<PackageItem[]>([]);

  orderRequest: OrderRequestItem = new OrderRequestItem({});
  orderRequest$ = new BehaviorSubject<OrderRequestItem>(this.orderRequest);

  // ----------
  // #endregion

  /////////////////////////////

  // #region -> Constructor <-
  // ----------

  constructor(
    private myProfileService: MyProfileService,
    private getAvailableItemsGQL: GetAvailableItemsGQL,
    private addOrderMutation: AddOrderGQL
  ) {}

  // ----------
  // #endregion

  /////////////////////////////

  // #region -> Method <-
  // ----------

  initPackageData(): Observable<void> {
    // Fetch the default location
    this.myProfileService.fetchLocationList();
    return this.myProfileService.preferredLocation$.pipe(
      skip(1),
      switchMap((preferredLocation) => {
        // Reset the order request
        this.resetOrderRequest();

        // Keep track on the preferred location
        this.preferredLocation = lodash.cloneDeep(preferredLocation);

        this.updateSelectedLocation(preferredLocation);
        return this.fetchAvailablePackages(preferredLocation);
      }),
      take(1)
    );
  }

  /**
   * Fetch the list of packages based on the "location selection".
   */
  fetchAvailablePackages(location: FranchiseLocation): Observable<void> {
    //
    this.packageList = [];

    return this.getAvailableItemsGQL.fetch({ locationId: location.id }).pipe(
      take(1),
      map((res) => {
        //
        if (res.data) {
          res.data.getAvailableItems.forEach((service) => {
            if (service.category.active) {
              // Here, we only need "Package" type
              if (service.type.id === ServiceTypes.Package) {
                this._mapToPackage(service);
              }
            }
          });

          // Cast the changes.
          this.packageList$.next(this.packageList);
        }
      }),
      take(1)
    );
  }

  resetOrderRequest() {
    this.orderRequest = new OrderRequestItem({});
    this.orderRequest$.next(this.orderRequest);
  }

  updateSelectedLocation(newLocation: FranchiseLocation) {
    //
    this.orderRequest = this.orderRequest.update({
      selectedLocation: lodash.cloneDeep(newLocation),
      selectedPackageList: [],
    });

    // Cast
    this.orderRequest$.next(this.orderRequest);
  }

  updateOrderRequest(newRequest: OrderRequestItem) {
    //
    this.orderRequest = this.orderRequest.update({ ...newRequest.json() });

    // Cast
    this.orderRequest$.next(this.orderRequest);
  }

  updateOrderId(newId: string) {
    this.orderRequest = this.orderRequest.update({ orderId: newId });

    // Cast
    this.orderRequest$.next(this.orderRequest);
  }

  canAddOrder(): boolean {
    //
    return (
      this.orderRequest.selectedLocation &&
      this.orderRequest.selectedPackageList.length > 0
    );
  }

  addOrder(): Observable<any> {
    /*
      `lines` is a list of "ID" for all "order items"
     */
    const lines = [];
    this.orderRequest.selectedPackageList.forEach((packageItem) => {
      lines.push({ itemId: packageItem.id, qty: 1 });
    });
    return this.addOrderMutation
      .mutate({
        cartSubmissionInput: {
          franchiseId: this.orderRequest.selectedLocation.id,
          lines,
        },
      })
      .pipe(map((res) => res.data.addOrder));
  }

  // ----------
  // #endregion

  /////////////////////////////

  // #region -> Private Method <-
  // ----------

  private _mapToPackage(item): void {
    //
    this.packageList.push({
      id: item.id,
      name: item.displayName,
      price: item.price,
      taxableAmount: this.calculateValue(item.items, item.price),
      code: item.code,
      label: item.label,
      taxable: item.taxable,
      description: item.description,
      items: item.items,
      // default state
      isSelected: false,
    });
  }

  // iterate through the subItems, calculate their value, then calculate their actual price
  private calculateValue(items: SubItem[], packagePrice: number): number {
    let value = 0;
    let taxableTotal = 0;
    for (const item of items) {
      item.value = item.quantity * item.item.price;
      value += item.value;
    }
    // calculate the actual price of each item as part of the package
    for (const item of items) {
      item.price = item.value * (packagePrice / value);
      if (item.item.taxable) {
        taxableTotal += item.price;
      }
    }
    return taxableTotal;
  }

  // ----------
  // #endregion

  /////////////////////////////
}
